/**
 * PomodoroNotifications - Visual notification overlay for Pomodoro session events.
 *
 * Renders:
 * - A banner/toast prompt when a session completes (with action to start next phase)
 * - A screen flash effect as a visual alternative to audio
 * - An ARIA live region that announces timer state changes to assistive technologies
 *
 * This component should be rendered at the App Shell level (or within the Focus module page)
 * so notifications appear regardless of the active module.
 *
 * @see Requirements 7.3, 14.7, 14.8
 */

import { usePomodoroNotifications } from '@/hooks/usePomodoroNotifications';

// ─── Component ───────────────────────────────────────────────────────────────

export function PomodoroNotifications() {
  const {
    showBanner,
    bannerMessage,
    bannerAction,
    ariaAnnouncement,
    isFlashing,
    dismissBanner,
    startNextPhase,
  } = usePomodoroNotifications();

  return (
    <>
      {/* ─── Screen Flash (visual alternative to audio) ─────────────────────── */}
      {isFlashing && (
        <div
          className="fixed inset-0 z-[9999] pointer-events-none animate-screen-flash bg-primary/20"
          aria-hidden="true"
        />
      )}

      {/* ─── Notification Banner / Toast ────────────────────────────────────── */}
      {showBanner && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-full max-w-md px-4"
          role="alertdialog"
          aria-modal="false"
          aria-label="Session complete notification"
        >
          <div
            className="
              bg-bg-card border border-primary/30 rounded-2xl shadow-lg
              p-4 flex flex-col gap-3
              animate-slide-in-top
              motion-reduce:animate-none motion-reduce:opacity-100
            "
          >
            {/* Message */}
            <p className="text-sm font-medium text-text-heading text-center">
              {bannerMessage}
            </p>

            {/* Action buttons */}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={startNextPhase}
                className="
                  px-4 py-2 rounded-xl text-sm font-medium
                  bg-primary text-white
                  hover:bg-primary-dark transition-colors duration-fast
                  focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2
                "
                autoFocus
              >
                {bannerAction}
              </button>
              <button
                onClick={dismissBanner}
                className="
                  px-4 py-2 rounded-xl text-sm font-medium
                  bg-bg-secondary text-text-secondary
                  hover:bg-border transition-colors duration-fast
                  focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2
                "
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ARIA Live Region for Timer State ───────────────────────────────── */}
      {/* This region announces timer state changes to screen readers.
          Updates are polite (won't interrupt current speech). */}
      <div
        aria-live="polite"
        aria-atomic="true"
        role="status"
        className="sr-only"
      >
        {ariaAnnouncement}
      </div>
    </>
  );
}

PomodoroNotifications.displayName = 'PomodoroNotifications';
